import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { LOGO_URL } from "@/lib/brand";
import GoogleTranslate from "./GoogleTranslate";

const TOUR_CATEGORIES = [
  "Golden Triangle", "Rajasthan Tours", "Kerala Tours", "Kashmir Tours",
  "Ladakh Tours", "South India Tours", "Himalayan Tours", "Wildlife Tours",
  "Spiritual Tours", "Luxury Tours", "Honeymoon Tours", "Family Tours",
];
const TOP_DEST = ["Kashmir", "Rajasthan", "Kerala", "Goa", "Agra", "Ladakh", "Varanasi", "Jaipur"];

export default function Header() {
  const { settings } = useSettings();
  const { openEnquiry } = useEnquiry();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(null);
  }, [loc.pathname]);

  const dark = scrolled || loc.pathname !== "/";
  const phone = settings?.phone || "+91 80773 85030";

  const linkColor = dark ? "text-[#3b1a1a]" : "text-white";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        dark
          ? "bg-[#fdfbf7] border-b border-[#d4af37]/25 shadow-[0_8px_30px_rgba(124,29,29,0.06)]"
          : "bg-gradient-to-b from-black/60 via-black/25 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-[74px]">
          {/* Brand */}
          <Link to="/" data-testid="header-brand" className="flex items-center gap-3 group shrink-0">
            <span className="inline-flex items-center bg-white rounded-xl p-1.5 shadow-sm border border-[#d4af37]/25 transition-transform duration-500 group-hover:scale-[1.03]">
              <img src={LOGO_URL} alt="Legacy India Travel" data-testid="header-logo" className="h-12 sm:h-14 w-auto object-contain" />
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            <Link to="/about-india" className={`text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>About India</Link>

            {/* Tours dropdown */}
            <div className="relative" onMouseEnter={() => setOpenDrop("tours")} onMouseLeave={() => setOpenDrop(null)}>
              <Link to="/tours" data-testid="nav-tours" className={`flex items-center gap-1 text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>
                Tours <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              {openDrop === "tours" && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div className="w-[520px] bg-[#fdfbf7] border border-[#d4af37]/30 shadow-2xl p-6 grid grid-cols-2 gap-x-6 gap-y-1">
                    {TOUR_CATEGORIES.map((c) => (
                      <Link key={c} to={`/tours?category=${encodeURIComponent(c)}`} className="text-sm text-[#3b1a1a] hover:text-[#7c1d1d] py-2 border-b border-[#d4af37]/10 transition-colors">
                        {c}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Destinations dropdown */}
            <div className="relative" onMouseEnter={() => setOpenDrop("dest")} onMouseLeave={() => setOpenDrop(null)}>
              <Link to="/destinations" data-testid="nav-destinations" className={`flex items-center gap-1 text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>
                Destinations <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              {openDrop === "dest" && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div className="w-[420px] bg-[#fdfbf7] border border-[#d4af37]/30 shadow-2xl p-6 grid grid-cols-2 gap-x-6 gap-y-1">
                    {TOP_DEST.map((d) => (
                      <Link key={d} to={`/destinations/${d.toLowerCase()}`} className="text-sm text-[#3b1a1a] hover:text-[#7c1d1d] py-2 border-b border-[#d4af37]/10 transition-colors">
                        {d}
                      </Link>
                    ))}
                    <Link to="/destinations" className="col-span-2 mt-2 text-xs tracking-[0.2em] uppercase text-[#7c1d1d]">View all destinations →</Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" className={`text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>Blog</Link>
            <Link to="/testimonials" className={`text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>Reviews</Link>
            <Link to="/contact" className={`text-[13px] tracking-[0.12em] uppercase hover:text-[#d4af37] transition-colors ${linkColor}`}>Contact</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <GoogleTranslate dark={dark} />
            <a href={`tel:${phone.replace(/\s/g, "")}`} className={`flex items-center gap-2 text-[13px] ${linkColor}`}>
              <Phone className="w-4 h-4 text-[#d4af37]" /> {phone}
            </a>
            <button
              data-testid="header-plan-trip"
              onClick={() => openEnquiry()}
              className="bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-6 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors border border-[#d4af37]/50"
            >
              Plan My Trip
            </button>
          </div>

          {/* Mobile lang switcher + toggle */}
          <div className="flex items-center gap-3 lg:hidden">
            <GoogleTranslate dark={dark} />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className={`inline-flex items-center justify-center w-11 h-11 border ${dark ? "border-[#d4af37]/40 text-[#3b1a1a]" : "border-white/40 text-white"}`}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#fdfbf7] border-t border-[#d4af37]/30 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            {[["About India", "/about-india"], ["Tours", "/tours"], ["Destinations", "/destinations"], ["Blog", "/blog"], ["Reviews", "/testimonials"], ["Contact", "/contact"], ["FAQ", "/faq"]].map(([label, to]) => (
              <Link key={to} to={to} className="text-sm tracking-[0.15em] uppercase text-[#3b1a1a] py-3 border-b border-[#d4af37]/15">{label}</Link>
            ))}
            <button onClick={() => { setMobileOpen(false); openEnquiry(); }} className="mt-4 bg-[#7c1d1d] text-white py-4 text-[11px] tracking-[0.22em] uppercase">Plan My Trip</button>
          </div>
        </div>
      )}
    </header>
  );
}
